/** Client ID access — buyer and seller stay on separate portals. */

import { ensureJourneyDb, getById, listTable, updateRow } from "../db";
import { clientPortalAllowed } from "./accountLifecycle";
import { logClientActivity } from "./clientActivity";

const TOKEN_KEY = "dh_client_tokens";

function digits(len) {
  const chars = "0123456789";
  let out = "";
  const bytes = new Uint8Array(len);
  crypto.getRandomValues(bytes);
  bytes.forEach((byte) => {
    out += chars[byte % chars.length];
  });
  return out;
}

export function generateClientCode(kind = "buyer") {
  const mark = kind === "seller" ? "SL" : "BY";
  return `RC-${mark}-${digits(4)}`;
}

export function normalizeClientCode(value) {
  return String(value || "")
    .trim()
    .toUpperCase()
    .replace(/\s+/g, "");
}

export function ensureClientCodes() {
  ensureJourneyDb();
  listTable("users")
    .filter((user) => user.role_type === "buyer" || user.role_type === "seller")
    .forEach((user) => {
      if (user.client_code) return;
      updateRow("users", user.id, { client_code: uniqueCode(user.role_type) });
    });
}

export function assignFreshClientCode(kind = "buyer") {
  ensureJourneyDb();
  return uniqueCode(kind);
}

function uniqueCode(kind = "buyer") {
  const used = new Set(
    listTable("users")
      .map((user) => normalizeClientCode(user.client_code))
      .filter(Boolean)
  );
  let next = generateClientCode(kind);
  let guard = 0;
  while (used.has(next) && guard < 20) {
    next = generateClientCode(kind);
    guard += 1;
  }
  return next;
}

export function getClientByCode(code) {
  ensureClientCodes();
  const needle = normalizeClientCode(code);
  if (!needle) return null;
  return (
    listTable("users").find(
      (user) =>
        (user.role_type === "buyer" || user.role_type === "seller") &&
        normalizeClientCode(user.client_code) === needle
    ) || null
  );
}

export function clientCodeForUser(userId) {
  ensureClientCodes();
  const user = getById("users", userId);
  return user?.client_code || "";
}

export function portalPathForClient(client) {
  if (!client) return "/";
  const code = encodeURIComponent(client.client_code);
  return client.role_type === "seller" ? `/seller/${code}` : `/buyer/${code}`;
}

function readTokens() {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_KEY) || "{}");
  } catch {
    return {};
  }
}

function writeTokens(map) {
  localStorage.setItem(TOKEN_KEY, JSON.stringify(map));
}

export function issueClientToken(clientId) {
  const token = `ct_${crypto.randomUUID().replace(/-/g, "").slice(0, 20)}`;
  const map = readTokens();
  map[token] = { clientId, at: new Date().toISOString() };
  writeTokens(map);
  return token;
}

export function clientFromToken(token) {
  const row = readTokens()[String(token || "")];
  if (!row?.clientId) return null;
  ensureClientCodes();
  return getById("users", row.clientId) || null;
}

export function portalAccessUrl(token) {
  if (typeof window === "undefined") return `/portal/access?token=${token}`;
  return `${window.location.origin}/portal/access?token=${token}`;
}

export function portalHrefForClient(client) {
  const path = portalPathForClient(client);
  if (typeof window === "undefined") return path;
  return `${window.location.origin}${path}`;
}

export function lookupClientAccess({ code, verify }) {
  const client = getClientByCode(code);
  if (!client) return { ok: false, error: "That Client ID was not found." };
  if (!clientPortalAllowed(client)) {
    return { ok: false, error: "This portal is paused. Please contact your advisor’s office." };
  }
  const hint = String(verify || "").trim().toLowerCase();
  if (hint) {
    const last = String(client.name || "").trim().split(/\s+/).pop() || "";
    const email = String(client.email || "").toLowerCase();
    if (hint !== last.toLowerCase() && hint !== email) {
      return { ok: false, error: "The name or email did not match this Client ID." };
    }
  }
  logClientActivity(client.id, "portal_opened", { clientCode: client.client_code });
  return { ok: true, client };
}
